import { MessageBar, Spinner } from '@fluentui/react'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CloudyResponseSummary } from 'types/orangebox'
import { CLOUDY_TIMEOUT_MS, createRequestScope } from './request'

export const Cloudy = () => {
  const location = useLocation()
  const [cloudyEnabled, setCloudyEnabled] = useState(false)
  const [cloudySummary, setCloudySummary] = useState<undefined | string>(undefined)
  const [cloudyDisposition, setCloudyDisposition] = useState<undefined | string>(undefined)
  const [error, setError] = useState<boolean | string>(false)

  useEffect(() => {
    setCloudyEnabled(location.hash.substring(1) === "cloudy_enabled")
  }, [location.hash])

  useEffect(() => {
    const clientId = new URL(window.location.href).searchParams.get("clientId")
    if (cloudyEnabled && !cloudySummary && clientId) {
      const item = Office.context.mailbox.item
      if (!item) return;
      const { internetMessageId: message_id, dateTimeCreated: timestamp } = item;
      const recipient = Office.context.mailbox.userProfile.emailAddress;
      const scope = createRequestScope(CLOUDY_TIMEOUT_MS);
      (async () => {
        try {
          if (!Office.context.requirements.isSetSupported("Mailbox", "1.8")) {
            setError("Cloudy summaries require a newer version of Outlook.")
            return;
          }

          const internet_message_headers = await new Promise<string>((resolve, reject) => {
            item.getAllInternetHeadersAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                resolve(result.value);
              } else {
                reject(new Error(result.error?.message ?? "getAllInternetHeadersAsync failed"));
              }
            });
          });

          window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Requesting Cloudy summary", level: "info" });
          const summaryRequest = await fetch(`/api/v1/office365/customers/cloudy_summary`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              token: clientId,
              message_id,
              timestamp,
              recipient,
              internet_message_headers,
            }),
            signal: scope.signal,
          });
          switch (summaryRequest.status) {
            case 404:
              window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Summary not found", level: "warning" });
              setError("No further information for this email.")
              break;
            case 200:
              const { result } = await summaryRequest.json<CloudyResponseSummary>();
              if (!result) {
                window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Summary response missing result", level: "error" });
                setError("Error loading summary.")
                break;
              }
              window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Summary loaded successfully", level: "info", data: { disposition: result.final_disposition } });
              setCloudySummary(result.summary)
              setCloudyDisposition(result.final_disposition == "NONE" ? "CLEAN" : result.final_disposition)
              break;
            case 400:
              window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Cloudy feature not enabled", level: "warning" });
              setError("Cloudy summaries feature is not enabled.")
              break;
            case 429:
              setError("Too many requests. Please wait a moment before trying again.")
              break;
            default:
              const { errors = [] } = await summaryRequest.json<CloudyResponseSummary>()
              window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Summary request failed", level: "error", data: { status: summaryRequest.status } });
              setError(errors[0]?.message ?? "Unknown Error")
          }
        } catch (e) {
          // undefined means the effect was cleaned up (unmount or dependency change): nothing to report.
          const message = scope.errorMessage(e)
          if (message !== undefined) {
            window.Sentry?.addBreadcrumb({ category: "cloudy", message: "Summary request threw", level: "error" });
            setError(message)
          }
        } finally {
          scope.settle()
        }
      })()
      return scope.dispose
    }
  }, [cloudyEnabled, cloudySummary])

  return cloudyEnabled ? <>
    <br />
    <MessageBar styles={{
      iconContainer: {
        position: "absolute",
        top: "0px",
        left: "0px"
      },
      root: {
        backgroundColor: "#FEF1E6",
        color: "#592b03",
        position: "relative",
        border: "2px solid #f7a054"
      }
    }}
      messageBarIconProps={{
        style: {
          background: "url('/assets/cloudy_logo.png')",
          backgroundSize: "cover",
          width: "1em",
          height: "1em",
        }
      }}>
      <div style={{
        paddingLeft: "2em"
      }}>Reasons summary by Cloudy</div>
      <div style={{
        margin: "15px 0px"
      }}>
        {!error && !cloudySummary && <Spinner styles={{
          label: {
            color: "#592b03",
            fontWeight: "bold"
          },
          circle: {
            borderColor: "#f68220 #ffffff #ffffff"
          }
        }} label="Checking For Summary" />}
        {error}
        {cloudyDisposition && <p>
          We think this email is <span style={{ textTransform: "capitalize", fontWeight: "bold" }}>{cloudyDisposition.toLocaleLowerCase()}</span> because:
        </p>}
        {cloudySummary}
      </div>
      {cloudySummary && <div style={{ fontSize: "0.8em" }}>Cloudflare's dashboard AI summarization may have inaccuracies. Confirm details before taking action. No customer data was used to generate this summary.</div>}
    </MessageBar></> : <></>
}
